import { Link } from "@/lib/router-compat";
import { FileText, ExternalLink } from "lucide-react";
import type { CdnProduct } from "@/lib/cdnProducts";

export interface ProductDoc {
  label: string;
  url: string;
}

/** Normalized document list for a product: the doc_links array plus the
 * install PDF (added only when it isn't already listed). */
export function productDocs(product: CdnProduct): ProductDoc[] {
  const docs: ProductDoc[] = [];
  const links = Array.isArray(product.DocLinks) ? product.DocLinks : [];
  for (const l of links) {
    const url = (l?.url || "").trim();
    if (!url) continue;
    if (docs.some((d) => d.url === url)) continue;
    docs.push({ label: (l?.label || "").trim() || "Document", url });
  }
  const install = (product.InstallPdfUrl || "").trim();
  if (install && !docs.some((d) => d.url === install)) {
    docs.push({ label: "Installation Instructions", url: install });
  }
  return docs;
}

export default function ProductDocuments({ product }: { product: CdnProduct }) {
  const docs = productDocs(product);
  if (docs.length === 0) return null;
  const slug = (product.short_slug || product.admin_slug || String(product.ProductID)) as string;

  return (
    <section className="mt-10">
      <h2 className="text-xl font-bold mb-3">Instructions &amp; Specs</h2>
      <ul className="divide-y divide-border rounded-lg border border-border bg-card">
        {docs.map((d, i) => (
          <li key={d.url}>
            <Link
              to={`/parts-catalog/docs/${slug}?doc=${i}`}
              className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-muted transition-colors"
            >
              <FileText className="h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
              <span className="flex-1 font-medium">{d.label}</span>
              <ExternalLink className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
